import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { getPaymentStatus } from '@/lib/api';
import { logger } from '@/lib/logger';

const MAX_ATTEMPTS = 15;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling'); // polling | paid | timeout | error
  const attempts = useRef(0);
  const refreshedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.data.payment_status === 'paid') {
          if (!refreshedRef.current) {
            refreshedRef.current = true;
            try { await refreshUser?.(); } catch (e) { logger.warn('refreshUser after payment failed', e); }
          }
          setStatus('paid');
          return;
        }
        if (res.data.payment_status === 'failed' || res.data.payment_status === 'expired') {
          setStatus('error');
          return;
        }
      } catch (e) { logger.warn('payment poll transient', e); }

      if (attempts.current >= MAX_ATTEMPTS) {
        setStatus('timeout');
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center py-12 px-4" data-testid="payment-success-page">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'polling' && (
            <>
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Bekræfter betaling...</h1>
              <p className="text-muted-foreground mb-6">Vent et øjeblik. Vi verificerer din betaling med Stripe.</p>
            </>
          )}

          {status === 'paid' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Betaling gennemført!</h1>
              <p className="text-muted-foreground mb-6">Dit abonnement er nu aktiveret.</p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => navigate('/abonnement')} data-testid="view-plans-button">
                  Se planer
                </Button>
                <Button className="flex-1" onClick={() => navigate(user?.user_type === 'company' ? '/company-dashboard' : '/')} data-testid="go-to-dashboard-button">
                  Gå til dashboard
                </Button>
              </div>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Verificerer stadig...</h1>
              <p className="text-muted-foreground mb-6">Din betaling er modtaget, men Stripe bekræfter stadig. Genindlæs siden om et minut, eller tjek dit dashboard.</p>
              <Button onClick={() => window.location.reload()} data-testid="retry-button">Prøv igen</Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Betaling kunne ikke bekræftes</h1>
              <p className="text-muted-foreground mb-6">Vi kunne ikke bekræfte din betaling. Prøv venligst igen.</p>
              <Button onClick={() => navigate('/abonnement')} data-testid="back-to-pricing-button">Tilbage til abonnement</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
